import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useBpV2State } from "../../../context/BusinessPlanV2Context";
import { GlobalState } from "../../../context/GlobalContext";
import { AuthState } from "context/AuthContext";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { grey } from "@mui/material/colors";
import { KeyboardBackspaceRounded, SyncRounded } from "@mui/icons-material";

import StoresGrid from "../StoresGrid/StoresGrid";
import PlanReport from "../PlanReport/PlanReport";
import StoreBpModal from "../StoreBpModal/StoreBpModal";
import { API } from "../data";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";

const pageSx = {
	width: "1280px",
	maxWidth: "100%",
	margin: "auto",
	height: "calc(100vh - 72px)",
	boxSizing: "border-box",
	py: 1.5,
	display: "flex",
	flexDirection: "column",
	overflow: "hidden",
};

const TABS = [
	{ value: "stores", label: "Stores" },
	{ value: "report", label: "Report" },
];

/** Segmented switcher — reads as a control, not as page text. */
const TabSwitch = ({ value, onChange }) => (
	<Stack
		direction="row"
		spacing={0.5}
		sx={{
			bgcolor: grey[100],
			border: `1px solid ${grey[200]}`,
			borderRadius: 2,
			p: 0.375,
			flexShrink: 0,
		}}
	>
		{TABS.map((t) => {
			const active = value === t.value;
			return (
				<Box
					key={t.value}
					role="tab"
					aria-selected={active}
					onClick={() => onChange(t.value)}
					sx={{
						px: 2,
						py: 0.5,
						borderRadius: 1.5,
						cursor: "pointer",
						userSelect: "none",
						fontSize: 12.5,
						fontWeight: 700,
						letterSpacing: "0.03em",
						color: active ? grey[900] : grey[600],
						bgcolor: active ? "#fff" : "transparent",
						boxShadow: active ? "0 1px 3px rgba(15, 23, 42, 0.16)" : "none",
						transition: "background-color 0.15s ease, color 0.15s ease",
						"&:hover": { color: grey[900] },
					}}
				>
					{t.label}
				</Box>
			);
		})}
	</Stack>
);

const cardSx = {
	backgroundColor: "white",
	border: `1px solid ${grey[200]}`,
	borderRadius: 2.5,
	boxShadow: "0 4px 18px rgba(15, 23, 42, 0.045)",
	p: 1.5,
	flex: 1,
	minHeight: 0,
	display: "flex",
	flexDirection: "column",
	overflow: "hidden",
};

const SelectedPlanPage = () => {
	const { planId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const { selectedPlan, setSelectedPlan, setSelectedStore, allBp, setAllBp } = useBpV2State();
	const { setLoadingOpen, setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const { user } = AuthState();
	const isAdmin = user?.role?.ADMIN === true;

	const tab = searchParams.get("tab") === "report" ? "report" : "stores";
	const [plan, setPlan] = useState(null);
	const [loadingPlan, setLoadingPlan] = useState(true);
	const [showStoreBpModal, setShowStoreBpModal] = useState(false);
	const [storesRefreshKey, setStoresRefreshKey] = useState(0);
	const [resyncConfirmOpen, setResyncConfirmOpen] = useState(false);
	const [resyncing, setResyncing] = useState(false);
	const [resyncResult, setResyncResult] = useState(null);

	useEffect(() => {
		let cancelled = false;

		const resolvePlan = async () => {
			if (!planId) {
				navigate("/explore/business_plan_v2", { replace: true });
				return;
			}

			const fromContext =
				(selectedPlan?.id === planId && selectedPlan) ||
				(allBp || []).find((p) => p.id === planId) ||
				null;

			if (fromContext) {
				if (!cancelled) {
					setPlan(fromContext);
					setSelectedPlan(fromContext);
					setLoadingPlan(false);
				}
				return;
			}

			try {
				setLoadingPlan(true);
				setLoadingOpen(true);
				const { data } = await axios.get(API.GET_PLANS);
				if (data.status === "success") {
					const plans = data.business_plan || [];
					setAllBp(plans);
					const found = plans.find((p) => p.id === planId);
					if (!found) {
						setSnackBarOpen(true);
						setSnackBar({ ...snackBar, message: "Plan not found", severity: "error" });
						navigate("/explore/business_plan_v2", { replace: true });
						return;
					}
					if (!cancelled) {
						setPlan(found);
						setSelectedPlan(found);
					}
				}
			} catch (err) {
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, message: parseErrMsgFromError(err), severity: "error" });
				navigate("/explore/business_plan_v2", { replace: true });
			} finally {
				if (!cancelled) setLoadingPlan(false);
				setLoadingOpen(false);
			}
		};

		resolvePlan();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [planId]);

	const handleTabChange = (value) => {
		const next = new URLSearchParams(searchParams);
		if (value === "report") next.set("tab", "report");
		else next.delete("tab");
		setSearchParams(next, { replace: true });
	};

	const handleBack = () => navigate("/explore/business_plan_v2");

	const handleStoreClick = (store) => {
		setSelectedStore(store);
		setShowStoreBpModal(true);
	};

	const handleManageSubmissions = (store) => {
		setSelectedStore(store);
		setShowStoreBpModal(true);
	};

	const handleResyncStores = async () => {
		if (!plan?.id) return;
		try {
			setResyncing(true);
			const { data } = await axios.post(API.RESYNC_PLAN_STORES(plan.id));
			if (data.status === "success") {
				setResyncResult(data.result);
				setStoresRefreshKey((k) => k + 1);
				setPlan((prev) => ({ ...prev, total_count: data.result?.total_active_stores ?? prev?.total_count }));
			}
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: parseErrMsgFromError(err), severity: "error" });
		} finally {
			setResyncing(false);
			setResyncConfirmOpen(false);
		}
	};

	if (loadingPlan || !plan) {
		return (
			<Box sx={{ ...pageSx, alignItems: "center", justifyContent: "center" }}>
				<CircularProgress />
			</Box>
		);
	}

	const planMeta = [plan?.channel, plan?.brand, plan?.season, plan?.year]
		.filter(Boolean)
		.join(" · ");

	return (
		<Box sx={pageSx}>
			<Box sx={cardSx}>
				{/* Three zones: back control · plan identity · view switcher */}
				<Stack
					direction="row"
					spacing={1.5}
					alignItems="center"
					flexShrink={0}
					sx={{ borderBottom: `1px solid ${grey[200]}`, pb: 1, mb: 1.25 }}
				>
					<Button
						size="small"
						variant="outlined"
						color="inherit"
						startIcon={<KeyboardBackspaceRounded sx={{ fontSize: 18 }} />}
						onClick={handleBack}
						sx={{
							flexShrink: 0,
							height: 32,
							px: 1.25,
							fontSize: 12,
							fontWeight: 700,
							color: grey[700],
							borderColor: grey[300],
							"&:hover": { borderColor: grey[400], bgcolor: grey[50] },
						}}
					>
						Plans
					</Button>

					<Divider orientation="vertical" flexItem sx={{ my: 0.5, borderColor: grey[200] }} />

					<Stack spacing={0} sx={{ minWidth: 0 }}>
						<Typography fontSize={15} fontWeight={700} color={grey[900]} lineHeight={1.3} noWrap>
							{plan?.name}
						</Typography>
						{planMeta ? (
							<Typography
								fontSize={10.5}
								fontWeight={600}
								color={grey[500]}
								lineHeight={1.3}
								noWrap
								sx={{ textTransform: "uppercase", letterSpacing: "0.07em" }}
							>
								{planMeta}
							</Typography>
						) : null}
					</Stack>

					<Box sx={{ ml: "auto" }} />

					{isAdmin && (
						<Tooltip title="Fetch the latest store list from BigQuery and reconcile it with this plan">
							<Button
								size="small"
								variant="outlined"
								color="inherit"
								startIcon={<SyncRounded sx={{ fontSize: 16 }} />}
								onClick={() => {
									setResyncResult(null);
									setResyncConfirmOpen(true);
								}}
								sx={{
									flexShrink: 0,
									height: 32,
									px: 1.25,
									fontSize: 12,
									fontWeight: 700,
									color: grey[700],
									borderColor: grey[300],
									"&:hover": { borderColor: grey[400], bgcolor: grey[50] },
								}}
							>
								Resync stores
							</Button>
						</Tooltip>
					)}

					<TabSwitch value={tab} onChange={handleTabChange} />
				</Stack>

				<Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
					{tab === "stores" ? (
						<StoresGrid
							plan={plan}
							onBack={handleBack}
							onStoreClick={handleStoreClick}
							onManageSubmissions={handleManageSubmissions}
							refreshKey={storesRefreshKey}
							hideHeader
						/>
					) : (
						<PlanReport plan={plan} />
					)}
				</Box>
			</Box>

			<StoreBpModal
				open={showStoreBpModal}
				onClose={() => {
					setShowStoreBpModal(false);
					setStoresRefreshKey((k) => k + 1);
				}}
			/>

			<Dialog open={resyncConfirmOpen} onClose={() => (!resyncing ? setResyncConfirmOpen(false) : null)} maxWidth="xs" fullWidth>
				<DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Resync stores from BigQuery?</DialogTitle>
				<DialogContent>
					<DialogContentText component="div" sx={{ fontSize: 13 }}>
						This fetches the current operational store list for <b>{plan?.channel}</b>
						{plan?.brand ? ` · ${plan.brand}` : ""} from BigQuery and reconciles it with this plan's
						stores:
						<Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
							<li>New stores in BigQuery are added to the plan.</li>
							<li>Stores no longer in BigQuery are flagged "Removed" (their submissions/history are kept).</li>
							<li>Previously removed stores that reappear are reactivated.</li>
						</Box>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button size="small" onClick={() => setResyncConfirmOpen(false)} disabled={resyncing}>
						Cancel
					</Button>
					<Button size="small" variant="contained" onClick={handleResyncStores} disabled={resyncing}>
						{resyncing ? "Resyncing…" : "Resync now"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={!!resyncResult} onClose={() => setResyncResult(null)} maxWidth="xs" fullWidth>
				<DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Resync complete</DialogTitle>
				<DialogContent>
					<Stack spacing={1}>
						<Stack direction="row" spacing={1} alignItems="center">
							<Chip size="small" color="success" label={`+${resyncResult?.added?.length || 0} added`} />
							<Chip size="small" color="error" label={`${resyncResult?.removed?.length || 0} removed`} />
							<Chip
								size="small"
								color="info"
								label={`${resyncResult?.reactivated?.length || 0} reactivated`}
							/>
						</Stack>
						<Typography variant="caption" color="text.secondary">
							{resyncResult?.updated_count || 0} existing store(s) refreshed ·{" "}
							{resyncResult?.total_active_stores ?? 0} active store(s) in plan
						</Typography>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button size="small" variant="contained" onClick={() => setResyncResult(null)}>
						Done
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default SelectedPlanPage;
